import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import StatusPill from "@/components/marketplace/StatusPill";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function CalendarPage() {
  const [anchor, setAnchor] = useState(startOfWeek(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [block, setBlock] = useState({ title: "Blocked", start: "", end: "", reason: "" });
  useDocumentTitle("Calendar");

  const load = () => {
    setLoading(true);
    const end = new Date(anchor.getTime() + 7 * 86400000);
    api.get("/calendar", { params: { start: anchor.toISOString(), end: end.toISOString() } })
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [anchor]);

  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(anchor.getTime() + i * 86400000);
      out.push({ date: d, iso: d.toISOString().slice(0, 10) });
    }
    return out;
  }, [anchor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const d = new Date(e.start).toISOString().slice(0, 10);
      map[d] = map[d] || [];
      map[d].push(e);
    });
    return map;
  }, [events]);

  const submitBlock = async () => {
    try {
      await api.post("/calendar/blocks", block);
      toast.success("Block added");
      setShowBlockForm(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };
  const deleteBlock = async (id) => {
    try { await api.delete(`/calendar/blocks/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error("Failed"); }
  };

  return (
    <DashboardShell>
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Calendar</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Week of {anchor.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button className="rounded-full border border-slate-200 h-9 w-9 flex items-center justify-center" onClick={() => setAnchor(new Date(anchor.getTime() - 7 * 86400000))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
            <button className="rounded-full border border-slate-200 px-4 py-2 text-sm" onClick={() => setAnchor(startOfWeek(new Date()))} data-testid="cal-today">Today</button>
            <button className="rounded-full border border-slate-200 h-9 w-9 flex items-center justify-center" onClick={() => setAnchor(new Date(anchor.getTime() + 7 * 86400000))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
            <button className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm inline-flex items-center gap-2" onClick={() => setShowBlockForm(true)} data-testid="cal-add-block"><Plus className="h-4 w-4" /> Block time</button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-3 min-h-[360px]">
          {days.map((d) => (
            <div key={d.iso} className="rounded-2xl border border-slate-200 bg-white p-3 flex flex-col" data-testid={`cal-day-${d.iso}`}>
              <div className="text-xs uppercase font-bold text-[#252A34]/60">{DOW[(d.date.getDay() + 6) % 7]}</div>
              <div className="font-display text-2xl text-[#071b22]">{d.date.getDate()}</div>
              <div className="mt-3 space-y-1.5 flex-1">
                {(eventsByDay[d.iso] || []).map((e) => (
                  <EventPill key={e.id} e={e} onDeleteBlock={deleteBlock} />
                ))}
                {(!eventsByDay[d.iso] || eventsByDay[d.iso].length === 0) && (
                  <div className="text-[11px] text-[#252A34]/40 italic">nothing scheduled</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {showBlockForm && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowBlockForm(false)}>
            <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              <div className="font-display text-lg font-semibold text-[#071b22] mb-4">Block time</div>
              <div className="space-y-3">
                <F label="Title"><input value={block.title} onChange={(e) => setBlock({ ...block, title: e.target.value })} className="cal-input" data-testid="block-title" /></F>
                <F label="Start"><input type="datetime-local" value={block.start} onChange={(e) => setBlock({ ...block, start: e.target.value })} className="cal-input" data-testid="block-start" /></F>
                <F label="End"><input type="datetime-local" value={block.end} onChange={(e) => setBlock({ ...block, end: e.target.value })} className="cal-input" data-testid="block-end" /></F>
                <F label="Reason (optional)"><input value={block.reason} onChange={(e) => setBlock({ ...block, reason: e.target.value })} className="cal-input" /></F>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setShowBlockForm(false)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                <button onClick={submitBlock} disabled={!block.start || !block.end} className="rounded-full bg-[#165a71] text-white px-5 py-2 text-sm font-medium disabled:opacity-60" data-testid="block-submit">Add block</button>
              </div>
              <style>{`
                .cal-input { width: 100%; border-radius: 0.75rem; border: 1px solid #e2e8f0; padding: 0.5rem 0.75rem; font-size: 0.875rem; }
              `}</style>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

function EventPill({ e, onDeleteBlock }) {
  if (e.kind === "block") {
    return (
      <div className="rounded-lg bg-slate-100 text-[#252A34] px-2 py-1 text-[11px] flex items-center justify-between">
        <span className="truncate">🚫 {e.title}</span>
        <button className="text-[#252A34]/60 hover:text-red-600" onClick={() => onDeleteBlock(e.id)} data-testid={`del-block-${e.id}`}>×</button>
      </div>
    );
  }
  const time = new Date(e.start).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return (
    <Link to={`/dashboard/bookings/${e.id}`} className="block rounded-lg bg-[#165a71]/10 text-[#071b22] px-2 py-1 text-[11px] hover:bg-[#165a71]/20" data-testid={`cal-event-${e.id}`}>
      <div className="font-medium truncate">{time} · {e.title}</div>
      <div className="text-[10px] text-[#252A34]/60 uppercase tracking-wider">{e.status?.replace(/_/g, " ")}</div>
    </Link>
  );
}

function startOfWeek(d) {
  const day = d.getDay(); // 0=Sun
  const diff = (day + 6) % 7; // days since Monday
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - diff);
  return start;
}

const F = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-medium text-[#252A34]/70 mb-1">{label}</div>
    {children}
  </label>
);
